import { useRef, useState, useEffect, useCallback } from 'react'
import { useDictation } from '../hooks/useDictation'
import { pwaApi, AnalyzeNoteResponse } from '../api'
import { MlotButton } from '../components/DuzyMic'
import { SearchBar } from '../components/SearchBar'
import { EmployeeList } from '../components/EmployeeList'
import { SyncStatusBar } from '../components/SyncStatusBar'
import type { ConversationState, ConversationActions } from '../hooks/useConversation'
import { useSearchV2 } from '../hooks/useSearchV2'
import { useOnline } from '../hooks/useNetwork'
import { collapseRepeatedPhrases, mergeFinalSegments } from '../utils/textSanitize'
import { setDictationActive } from '../pwaUpdate'
import { getQueue, isSyncInProgress } from '../engines/offline_pipeline'

function _t(freq: number, vol: number, durS: number) {
  try {
    const ctx  = new (window.AudioContext || (window as any).webkitAudioContext)()
    const osc  = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain); gain.connect(ctx.destination)
    osc.frequency.value = freq; osc.type = 'sine'
    gain.gain.setValueAtTime(vol, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + durS)
    osc.start(); osc.stop(ctx.currentTime + durS)
    osc.onended = () => ctx.close()
  } catch (_) {}
}
const sigTimeout  = () => { _t(880, 0.15, 0.08); setTimeout(() => { _t(660, 0.25, 0.08); setTimeout(() => _t(440, 0.18, 0.1), 100) }, 150) } // F2

interface Screen1Positions {
  titleTop: number
  employeeListTop: number
  employeeListTopWorking: number
  searchBarTop: number
  searchBarBottomExpand: number  // Miejsce na rozwinięcie SearchBar w GÓRĘ
  mlotButtonTop: number
  tokenBarBottom: number
}

interface Props {
  positions: Screen1Positions
  micScale: number
  conv: ConversationState & ConversationActions
  onNavigateTo2: (fullText: string, name: string, analyzePromise?: Promise<AnalyzeNoteResponse>) => Promise<void>
  onResetSession: () => void
  onUnauthorized: (reason: string) => void
  isActive: boolean
  resetSignal: number
  dbg: (msg: string) => void
}

export function Screen1({ positions, micScale, conv, onNavigateTo2, onResetSession, onUnauthorized, isActive, resetSignal, dbg }: Props) {
  const [micStatus, setMicStatus]   = useState<'default' | 'pushed' | 'working'>('default')

  // Bezpiecznik auto-aktualizacji PWA: nie przeładuj strony w trakcie dyktowania/obróbki.
  // Cleanup przy odmontowaniu jest konieczny — bez niego zejście z ekranu w trakcie
  // dyktowania zostawiało globalną flagę "true" na zawsze i PWA nigdy więcej się nie aktualizowało.
  useEffect(() => {
    setDictationActive(micStatus === 'working')
    return () => setDictationActive(false)
  }, [micStatus])
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [searchAnimated, setSearchAnimated] = useState(false)
  const [searchCompleted, setSearchCompleted] = useState(false)
  const search = useSearchV2()
  const isOnline = useOnline()
  const [preparingPhrase, setPreparingPhrase] = useState<string | null>(null)
  const [preparingPhraseColor, setPreparingPhraseColor] = useState<'green' | 'red'>('green')
  const [listModalOpen, setListModalOpen] = useState(false)
  // Ile notatek czeka na synchronizację z backendem + czy sync trwa właśnie teraz —
  // widoczny dowód, że coś faktycznie się dzieje, żeby nie trzeba było zgadywać.
  const [pendingSyncCount, setPendingSyncCount] = useState(0)
  const [syncingNow, setSyncingNow] = useState(false)
  useEffect(() => {
    const check = () => {
      getQueue().then(q => setPendingSyncCount(q.length)).catch(() => {})
      setSyncingNow(isSyncInProgress())
    }
    check()
    const interval = setInterval(check, 3000)
    return () => clearInterval(interval)
  }, [])

  const magicTimersRef  = useRef<ReturnType<typeof setTimeout>[]>([])
  const magicActiveRef  = useRef(false)
  const magicStartTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Lista ładowana przez App.tsx po zalogowaniu

  const voiceSearchTimer  = useRef<ReturnType<typeof setTimeout> | null>(null)
  const speechRef         = useRef<any>(null)
  const offlineSpeechRef  = useRef<any>(null)
  const dictTimer60       = useRef<ReturnType<typeof setTimeout> | null>(null)
  const detectedNameRef        = useRef<string | null>(null)
  const selectedEmployeeRef    = useRef<string>('')   // sync shadow dla conv.selectedEmployee
  const analyzePromiseRef      = useRef<Promise<AnalyzeNoteResponse> | null>(null)
  const heuristicNameRef       = useRef<string | null>(null)  // imię wykryte heurystyką w onSegment
  const searchCooldownRef = useRef(false)  // blokada 1s po wynikach wyszukiwania
  const llmRunningRef     = useRef<boolean>(false)
  const onDoneRef         = useRef<(full: string) => void>(() => {})
  const silenceTimerRef   = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingDoneRef    = useRef<string | null>(null)
  const pendingNavigateRef = useRef<string | null>(null)  // tekst czekający na zakończenie LLM
  const idleTimerRef           = useRef<ReturnType<typeof setTimeout> | null>(null)
  const idleResetRef            = useRef<() => void>(() => {})
  const searchResultsActiveRef  = useRef(false)
  const searchAnimatingRef      = useRef(false)  // true = trwa pipeline animacji wyszukiwania (kroki 1-9)

  const MAGIC_PHRASES = [
    'Analizuję kontekst…',
    'Dobieram model rozmowy…',
    'Kalibruję strategię…',
    'Generuję kroki…',
    'Finalizuję…',
  ]
  const MAGIC_BASE_DELAYS = [800, 1000, 900, 1100, 800]

  // UWAGA: Regex imion USUNIĘTY - zawsze używamy wyszukiwania kontekstowego RAG
  // extractNameFromQuery zostało usunięte aby uniknąć chaosu w pipeline

  // Heurystyka: szuka polskiego imienia gdziekolwiek w tekście (nie tylko na początku)
  // Imię = słowo z wielkiej litery z polskimi znakami, min 3 znaki, nie Pan/Pani/To/To/Jest/Nie
  const HEUR_SKIP = new Set(['Pan', 'Pani', 'To', 'Ten', 'Ta', 'Nie', 'Tak', 'Jest', 'Był', 'Była', 'Dzisiaj', 'Dziś', 'Wczoraj'])
  const extractNameHeuristic = (text: string): string | null => {
    const words = text.split(/\s+/)
    for (let i = 1; i < words.length; i++) {
      const w = words[i].replace(/[.,!?;:"""''()\-]/g, '')
      if (w.length < 3) continue
      if (!/^[A-ZŁŚŹŻĆŃÓĄĘ][a-złśźżćńóąę]{2,}$/.test(w)) continue
      if (HEUR_SKIP.has(w)) continue
      return w
    }
    // Fallback: sprawdź też pierwsze słowo
    if (words.length > 0) {
      const w = words[0].replace(/[.,!?;:"""''()\-]/g, '')
      if (w.length >= 3 && /^[A-ZŁŚŹŻĆŃÓĄĘ][a-złśźżćńóąę]{2,}$/.test(w) && !HEUR_SKIP.has(w)) {
        return w
      }
    }
    return null
  }

  const stopMagicPhrases = useCallback(() => {
    magicActiveRef.current = false
    magicTimersRef.current.forEach(t => clearTimeout(t))
    magicTimersRef.current = []
    if (magicStartTimerRef.current) {
      clearTimeout(magicStartTimerRef.current)
      magicStartTimerRef.current = null
    }
    setPreparingPhrase(null)
    setPreparingPhraseColor('green')
  }, [])

  const startMagicPhrases = useCallback(() => {
    stopMagicPhrases()
    magicActiveRef.current = true
    let cumDelay = 0
    MAGIC_PHRASES.forEach((phrase, i) => {
      const jitter = Math.floor(Math.random() * 160) - 80
      cumDelay += (MAGIC_BASE_DELAYS[i] ?? 1000) + jitter
      const t = setTimeout(() => {
        if (!magicActiveRef.current) return
        setPreparingPhrase(phrase)
      }, cumDelay)
      magicTimersRef.current.push(t)
    })
  }, [stopMagicPhrases])

  const startSpeech = (onResult: (text: string) => void, onEnd: () => void) => {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    if (!SR) { onEnd(); return null }
    const sr = new SR()
    let gotResult = false
    sr.lang = 'pl-PL'; sr.continuous = false; sr.interimResults = false
    sr.onresult = (e: any) => { gotResult = true; onResult(e.results[0]?.[0]?.transcript ?? '') }
    sr.onerror  = () => { if (!gotResult) onEnd() }
    sr.onend    = () => { if (!gotResult) onEnd() }
    sr.start()
    return sr
  }

  const startOfflineDictation = useCallback(() => {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    if (!SR) { dbg('NO_LOCAL_SR'); setMicStatus('default'); onDoneRef.current(''); return }
    const rec = new SR()
    rec.lang = 'pl-PL'
    rec.continuous = true
    rec.interimResults = true
    let final = ''
    let interim = ''
    offlineSpeechRef.current = rec
    rec.onresult = (e: any) => {
      const currentFinals: string[] = []
      let currentInterim = ''
      for (let i = 0; i < e.results.length; i++) {
        const t = e.results[i][0].transcript
        if (e.results[i].isFinal) {
          currentFinals.push(t)
        } else {
          currentInterim = t
        }
      }
      final = mergeFinalSegments(currentFinals)
      interim = currentInterim
      const full = (final + (interim ? ' ' + interim : '')).trim()
      if (!heuristicNameRef.current && !selectedEmployeeRef.current) {
        const hName = extractNameHeuristic(full)
        if (hName) {
          heuristicNameRef.current = hName
          dbg('HEUR_NAME:' + hName)
        }
      }
      const name = detectedNameRef.current || heuristicNameRef.current || conv.selectedEmployee
      const prefix = name ? `${name}: ` : ''
      conv.setTranscriptionText(prefix + full)
      if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null }
    }
    rec.onerror = (e: any) => { dbg('OFFLINE_SR_ERR:' + e.error) }
    rec.onend = () => {
      offlineSpeechRef.current = null
      const full = (final + (interim ? ' ' + interim : '')).trim()
      onDoneRef.current(collapseRepeatedPhrases(full))
    }
    try { rec.start() } catch (_) { dbg('OFFLINE_SR_START_ERR') }
  }, [conv, dbg])

  const IDLE_MS = 4000

  // ─── RESET UI (tylko wiz.) ──────────────────────────────────────────────
  const resetUI = () => {
    if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null }
    stopMagicPhrases()
    setSearchQuery('')
    setSearchAnimated(false)  // ← Reset trybu animacji LLM
    setMicStatus('default')
  }

  // ─── RESET WYNIKÓW WYSZUKIWANIA → przywróć pełną listę konwersacji
  const resetSearchResults = () => {
    searchResultsActiveRef.current = false
    searchAnimatingRef.current = false  // Odblokowanie TAP
    search.forceResetSearch()
    setSearchQuery('')
    setSearchAnimated(false)
    setSearchCompleted(false)
    setMicStatus('default')
    if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null }
    pwaApi.getConversations().then(r => conv.setConversationItems(r.items)).catch(() => {})
  }


  // ─── RESET SESJI (pełny) ─────────────────────────────────────────────
  const resetE1 = () => {
    if (searchResultsActiveRef.current) {
      resetSearchResults()
    } else {
      resetUI()
      onResetSession()
    }
  }

  idleResetRef.current = resetE1

  // Gdy E1 staje się nieaktywny — zatrzymaj TYLKO micStatus; frazy magiczne
  // niech odjeżdżają razem z E1 w lewo podczas animacji slide-left
  useEffect(() => {
    if (!isActive) {
      setMicStatus('default')
    }
  }, [isActive])

  // Gdy NOT_BUSINESS, MOBBING_WARNING lub PRIVACY_WARNING: zatrzymaj frazy magiczne, pokaż komunikat, zresetuj UI
  useEffect(() => {
    if (conv.guardStatus === 'NOT_BUSINESS') {
      stopMagicPhrases()
      setPreparingPhraseColor('red')
      setPreparingPhrase('To nie jest notatka służbowa — anulowano.')
      const t = setTimeout(() => {
        setPreparingPhrase(null)
        conv.resetConversation()
        setMicStatus('default')
      }, 2500)
      return () => clearTimeout(t)
    }
    if (conv.guardStatus === 'MOBBING_WARNING') {
      stopMagicPhrases()
      setPreparingPhraseColor('red')
      setPreparingPhrase('Wykryto treści mobbingowe — sformułuj notatkę inaczej.')
      const t = setTimeout(() => {
        setPreparingPhrase(null)
        conv.resetConversation()
        setMicStatus('default')
      }, 3000)
      return () => clearTimeout(t)
    }
    if (conv.guardStatus === 'PRIVACY_WARNING') {
      stopMagicPhrases()
      setPreparingPhraseColor('red')
      setPreparingPhrase('Wykryto dane wrażliwe — usuń informacje medyczne/prywatne.')
      const t = setTimeout(() => {
        setPreparingPhrase(null)
        conv.resetConversation()
        setMicStatus('default')
      }, 3000)
      return () => clearTimeout(t)
    }
  }, [conv.guardStatus])

  // Gdy zewnętrzny reset (np. powrót E3→E1) — zatrzymaj frazy magiczne, wyczyść wyszukiwanie
  useEffect(() => {
    if (resetSignal === 0) return
    stopMagicPhrases()
    setMicStatus('default')
    search.forceResetSearch()
    searchAnimatingRef.current = false
    searchResultsActiveRef.current = false
    setSearchQuery('')
    setSearchAnimated(false)
    setSearchCompleted(false)
  }, [resetSignal])

  // ← POPRAWKA: Reset searchAnimated gdy searchQuery zostaje wyczyszczony
  useEffect(() => {
    if (!searchQuery && searchAnimated) {
      setSearchAnimated(false)
      setSearchCompleted(false)
    }
  }, [searchQuery, searchAnimated])

  const clearIdleTimer = () => {
    if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null }
  }

  // Resetuj idle timer przy każdej interakcji z listą
  const resetIdleTimer = () => {
    if (micStatus === 'working' || micStatus === 'pushed') return
    clearIdleTimer()
    if (!conv.selectedEmployee && !searchQuery) return
    idleTimerRef.current = setTimeout(() => {
      idleTimerRef.current = null
      idleResetRef.current()
    }, IDLE_MS)
  }

  // Idle timer startuje gdy coś jest wybrane/wyszukane i mic jest idle
  // NIE startuje gdy trwa pipeline wyszukiwania RAG (searchAnimatingRef.current)
  useEffect(() => {
    if (!isActive) { clearIdleTimer(); return }   // nie odpala gdy E1 niewidoczny
    if (micStatus === 'working' || micStatus === 'pushed') { clearIdleTimer(); return }
    if (searchAnimatingRef.current) { clearIdleTimer(); return }  // pipeline aktywny — brak idle timera
    if (!conv.selectedEmployee && !searchQuery) { clearIdleTimer(); return }
    clearIdleTimer()
    idleTimerRef.current = setTimeout(() => {
      idleTimerRef.current = null
      idleResetRef.current()
    }, IDLE_MS)
    return () => { if (idleTimerRef.current) clearTimeout(idleTimerRef.current) }
  }, [conv.selectedEmployee, searchQuery, micStatus, isActive, search.state.phase])

  const handleVoiceSearch = (onDone: () => void) => {
    // Wyniki widoczne — tap zamyka i wraca do idle
    if (search.state.phase === 'results') {
      search.dismissSearch()
      searchAnimatingRef.current = false
      searchResultsActiveRef.current = false
      onDone()
      return
    }
    if (search.state.phase === 'searching') return
    startVoiceSearch(onDone)
  }

  const startVoiceSearch = (onDone: () => void) => {
    setSearchQuery('')
    setMicStatus('pushed')
    if (voiceSearchTimer.current) clearTimeout(voiceSearchTimer.current)
    if (speechRef.current) { try { speechRef.current.stop() } catch(_){} }
    speechRef.current = startSpeech(
      (text) => { handleVoiceSearchResult(text, onDone) },
      () => { onDone(); setMicStatus('default') }
    )
    voiceSearchTimer.current = setTimeout(() => {
      if (speechRef.current) { try { speechRef.current.stop() } catch(_){} }
      onDone(); setMicStatus('default')
    }, 5000)
  }

  const handleVoiceSearchResult = (text: string, onDone?: () => void) => {
    if (voiceSearchTimer.current) clearTimeout(voiceSearchTimer.current)
    const q = text.trim()
    if (!q) { if (onDone) onDone(); setMicStatus('default'); return }

    setMicStatus('default')
    if (onDone) onDone()
    searchCooldownRef.current = true
    setTimeout(() => { searchCooldownRef.current = false }, 1000)

    // ZAWSZE używamy wyszukiwania kontekstowego RAG (useSearchV2 → SearchAnimationPipeline)
    setSearchAnimated(true)
    setSearchCompleted(false)
    searchAnimatingRef.current = true  // Blokada TAP do kroku 9
    setSearchQuery('')
    setTimeout(() => setSearchQuery(q), 0)

    console.log('[RAG] Starting contextual search via useSearchV2 for:', q)
    search.startSearch(q)
  }

  const handleDeleteEmployee = (id: number) => {
    pwaApi.deleteEmployee(id).catch(() => {})
    conv.setApiEmployees(prev => prev ? prev.filter(e => e.id !== id) : prev)
  }

  const handleDeleteConversation = (id: number) => {
    pwaApi.deleteConversationItem(id).catch(() => {})
    conv.setConversationItems(prev => prev ? prev.filter(c => c.conversation_id !== id) : prev)
  }

  // Wywoływane gdy EmployeeList zakończy pipeline 9-krokowy (krok 9) - odblokowanie TAP
  const handleSearchAnimationEnd = () => {
    searchAnimatingRef.current = false
    console.log('[TAP] Unblocked - search animation completed')
  }

  // Failsafe: odblokuj TAP gdy wyszukiwanie wraca do idle
  useEffect(() => {
    if (search.state.phase === 'idle') {
      searchAnimatingRef.current = false
    }
  }, [search.state.phase])

  const doNavigate = (fullText: string) => {
    console.log('[Screen1] doNavigate called with:', fullText.slice(0, 50))
    pendingNavigateRef.current = null
    // Kolejność priorytetów: heurystyka > LLM detected > user selected > fallback
    const name = heuristicNameRef.current || detectedNameRef.current || selectedEmployeeRef.current || 'PracownikX'
    const ap   = analyzePromiseRef.current
    console.log('[Screen1] doNavigate - name:', name, 'has ap:', !!ap)
    heuristicNameRef.current    = null
    detectedNameRef.current     = null
    llmRunningRef.current       = false
    selectedEmployeeRef.current = ''  // czyść po nawigacji
    analyzePromiseRef.current   = null
    setSearchQuery('')
    console.log('[Screen1] doNavigate - calling onNavigateTo2')
    onNavigateTo2(fullText, name, ap ?? undefined)
  }

  const triggerNavigate = (fullText: string) => {
    console.log('[Screen1] triggerNavigate called, llmRunning:', llmRunningRef.current)
    // Jeśli analyzeNote jeszcze trwa (scenariusz C: brak heurystyki) — zaczekaj max 3s
    // Scenariusz A/B: heurystyka lub user selected — nawigacja natychmiast (llmRunningRef = false)
    if (llmRunningRef.current) {
      console.log('[Screen1] triggerNavigate - waiting for LLM (3s timeout)')
      pendingNavigateRef.current = fullText
      const deadline = setTimeout(() => {
        if (pendingNavigateRef.current) doNavigate(pendingNavigateRef.current)
      }, 3000)  // max 3s na LLM (zamiast 8s)
      // Wyczyść deadline gdy LLM skończy wcześniej (obsługiwane w onSpeechFinal)
      ;(window as any).__mlotLlmDeadline = deadline
      return
    }
    console.log('[Screen1] triggerNavigate - navigating immediately')
    doNavigate(fullText)
  }

  onDoneRef.current = (fullText: string) => {
    console.log('[Screen1] onDoneRef called with:', fullText.slice(0, 50))
    pendingDoneRef.current = fullText
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current)
    }
    // R2: adaptive timer — jeśli imię znane (heurystyka, LLM, lub user wybrał)
    // skracamy oczekiwanie do 500ms (user widział tekst w czasie rzeczywistym)
    // w przeciwnym razie — standardowe 2000ms (czeka na LLM max 3s w triggerNavigate)
    const hasName = heuristicNameRef.current || detectedNameRef.current || selectedEmployeeRef.current
    const delay = (!llmRunningRef.current && hasName) ? 500 : 2000
    dbg('DONE_DELAY:' + delay)
    console.log('[Screen1] onDoneRef - starting silence timer, delay:', delay, 'hasName:', hasName)
    silenceTimerRef.current = setTimeout(() => {
      silenceTimerRef.current = null
      const text = (pendingDoneRef.current ?? fullText).trim()
      pendingDoneRef.current = null
      console.log('[Screen1] silence timer fired, text length:', text.length)
      if (text.length < 3) { dbg('SKIP — za krótki tekst: ' + text.length); setMicStatus('default'); return }
      // Frazy magiczne startują po 250ms od końca timera
      console.log('[Screen1] starting magic phrases and navigation')
      if (magicStartTimerRef.current) clearTimeout(magicStartTimerRef.current)
      magicStartTimerRef.current = setTimeout(() => {
        magicStartTimerRef.current = null
        startMagicPhrases()
      }, 250)
      triggerNavigate(text)
    }, delay)
  }

  const dictation = useDictation({
    onSegment: (_text, full) => {
      dbg('SEG[' + full.length + ']:' + full.slice(0, 40))
      // Heurystyka: szukaj imienia w tekście w czasie rzeczywistym (bez czekania na LLM)
      if (!heuristicNameRef.current && !selectedEmployeeRef.current) {
        const hName = extractNameHeuristic(full)
        if (hName) {
          heuristicNameRef.current = hName
          dbg('HEUR_NAME:' + hName)
        }
      }
      const name = detectedNameRef.current || heuristicNameRef.current || conv.selectedEmployee
      const prefix = name ? `${name}: ` : ''
      conv.setTranscriptionText(prefix + full)
      // Resetuj silence timer — tekst nadal płynie
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current)
        silenceTimerRef.current = null
      }
    },
    onSpeechFinal: (_text, full) => {
      dbg('FINAL:' + full.slice(0, 30))
      if (!isOnline) return
      dbg(`FINAL_CHK sel=${!!selectedEmployeeRef.current} heur=${!!heuristicNameRef.current} det=${!!detectedNameRef.current}`)

      // Scenariusz A: user wybrał pracownika z listy — nie potrzebujemy LLM
      if (selectedEmployeeRef.current) {
        dbg('FINAL_SKIP — user selected')
        return
      }

      // Scenariusz B: heurystyka znalazła imię — użyj natychmiast, waliduj w tle
      if (heuristicNameRef.current) {
        dbg('FINAL_HEUR:' + heuristicNameRef.current)
        selectedEmployeeRef.current = heuristicNameRef.current
        conv.setSelectedEmployee(heuristicNameRef.current)
        conv.setTranscriptionText(`${heuristicNameRef.current}: ${full}`)

        // Walidacja w tle: sprawdź czy LLM zgadza się z heurystyką
        const ap = pwaApi.analyzeNote(full)
        analyzePromiseRef.current = ap
        ap.then(result => {
          if (result.detectedName && result.detectedName !== 'PracownikX' && result.detectedName !== heuristicNameRef.current) {
            // LLM zwrócił inne imię niż heurystyka — popraw w tle (user nie widzi opóźnienia)
            dbg('HEUR_CORRECT:' + result.detectedName + ' (was ' + heuristicNameRef.current + ')')
            detectedNameRef.current = result.detectedName
            // TODO: updateEmployee w tle (implementacja w useConversation)
          }
        }).catch(() => {})
        return
      }

      // Scenariusz C: brak heurystyki — czekamy na LLM (max 3s)
      if (detectedNameRef.current || llmRunningRef.current) {
        dbg('FINAL_SKIP — already running')
        return
      }

      llmRunningRef.current = true
      dbg('FINAL_CALLING_LLM')
      const ap = pwaApi.analyzeNote(full)
      analyzePromiseRef.current = ap
      ap.then(result => {
          llmRunningRef.current = false
          if (result.detectedName && result.detectedName !== 'PracownikX') {
            detectedNameRef.current = result.detectedName
            selectedEmployeeRef.current = result.detectedName
            conv.setSelectedEmployee(result.detectedName)
            conv.setTranscriptionText(`${result.detectedName}: ${result.cleanText || full}`)
          }
          // Jeśli nawigacja czekała na ten wynik — odpal teraz
          if (pendingNavigateRef.current) {
            if ((window as any).__mlotLlmDeadline) { clearTimeout((window as any).__mlotLlmDeadline); (window as any).__mlotLlmDeadline = null }
            doNavigate(pendingNavigateRef.current)
          }
        })
        .catch(() => {
          llmRunningRef.current = false
          if (pendingNavigateRef.current) {
            if ((window as any).__mlotLlmDeadline) { clearTimeout((window as any).__mlotLlmDeadline); (window as any).__mlotLlmDeadline = null }
            doNavigate(pendingNavigateRef.current)
          }
        })
    },
    onDone:  (full) => { dbg('DONE:' + full.slice(0, 30)); onDoneRef.current(full) },
    onError: (msg) => {
      dbg('ERR:' + msg.slice(0, 40))
      if (dictTimer60.current) clearTimeout(dictTimer60.current)
      setMicStatus('default')
      if (msg?.includes('403')) onUnauthorized('Subskrypcja wygasła lub konto zostało dezaktywowane.')
    },
  })

  // Czeka (max 10s, sprawdzając co 300ms), aż synchronizacja się skończy, PRZED
  // rozpoczęciem dyktowania — nie przerywa niczego, co już trwa, tylko odkłada
  // start o krótką chwilę w rzadkim przypadku, gdy akurat trwa sync. W typowym
  // przypadku (sync nieaktywny) zwraca się natychmiast, bez żadnej zwłoki.
  const waitForSyncToFinish = async (): Promise<void> => {
    const SYNC_WAIT_MAX_MS = 10000
    const SYNC_WAIT_STEP_MS = 300
    let waited = 0
    while (isSyncInProgress() && waited < SYNC_WAIT_MAX_MS) {
      await new Promise(r => setTimeout(r, SYNC_WAIT_STEP_MS))
      waited += SYNC_WAIT_STEP_MS
    }
  }

  const handleDictationStart = async () => {
    if (searchCooldownRef.current) { dbg('COOLDOWN — ignoruj start'); return }
    await waitForSyncToFinish()
    dbg('START')
    detectedNameRef.current     = null
    heuristicNameRef.current    = null  // czyść heurystykę przy nowej dyktacji
    llmRunningRef.current       = false
    selectedEmployeeRef.current = ''  // sync — czyść przed startem nagrywania
    analyzePromiseRef.current   = null
    // Wyczyść search, transkrypt i selectedEmployee — nowa dyktacja = nowa sesja imienia
    setSearchQuery('')
    conv.setTranscriptionText('')
    conv.setSelectedEmployee('')
    setMicStatus('working')
    if (isOnline) { dictation.start() } else { startOfflineDictation() }
    if (dictTimer60.current) clearTimeout(dictTimer60.current)
    dictTimer60.current = setTimeout(() => {
      sigTimeout()
      try { navigator.vibrate?.(200) } catch (_) {}
      handleDictationEnd()
    }, 45000)
  }

  const handleDictationEnd = () => {
    dbg('END')
    console.log('[Screen1] handleDictationEnd called')
    if (dictTimer60.current) clearTimeout(dictTimer60.current)
    if (isOnline) { dictation.stop() } else { offlineSpeechRef.current?.stop(); offlineSpeechRef.current = null }
  }

  return (
    <div className="relative w-full h-full">

      {preparingPhrase && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 50,
          background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none',
        }}>
          <div key={preparingPhrase} style={{ textAlign: 'center' }}>
            {(() => {
              const parts = preparingPhrase.split(' — ');
              return (
                <>
                  <span style={{
                    color: preparingPhraseColor === 'red' ? 'rgba(255,59,48,0.95)' : 'rgba(0,255,85,0.85)',
                    fontSize: '24px', fontWeight: 300,
                    fontFamily: '"Inter", sans-serif',
                    letterSpacing: '0.5px',
                    display: 'block',
                    marginBottom: '8px',
                    animation: 'magicFadeIn 0.5s ease forwards',
                  }}>{parts[0]}</span>
                  {parts[1] && (
                    <span style={{
                      color: preparingPhraseColor === 'red' ? 'rgba(255,59,48,0.75)' : 'rgba(0,255,85,0.7)',
                      fontSize: '18px', fontWeight: 300,
                      fontFamily: '"Inter", sans-serif',
                      letterSpacing: '0.3px',
                      display: 'block',
                      animation: 'magicFadeIn 0.5s ease forwards',
                    }}>{parts[1]}</span>
                  )}
                </>
              );
            })()}
          </div>
          <style>{`@keyframes magicFadeIn { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }`}</style>
        </div>
      )}

      {/* E1: Tytuł na górze */}
      <div style={{
        position: 'absolute', top: `${positions.titleTop}%`, left: 0, right: 0,
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        color: '#D9D9D9', textAlign: 'center',
        fontFamily: '"Inter", sans-serif', fontSize: 'clamp(24px, 4vw, 32px)',
        fontWeight: 200, letterSpacing: '3px',
        fontFeatureSettings: "'liga' off, 'clig' off", whiteSpace: 'nowrap',
      }}>BlackApp</div>

      {/* E1: Lista kafelków - przesuwa się w GÓRĘ zsynchronizowana z SearchBar (600ms + 200ms delay) */}
      <div style={{
        position: 'absolute',
        top: `${micStatus === 'working' ? positions.employeeListTopWorking : positions.employeeListTop}%`,
        left: '4.8%', right: '4.8%',
        zIndex: listModalOpen ? 60 : 20,
        transition: micStatus === 'working'
          ? 'top 600ms cubic-bezier(0.25, 1, 0.2, 1) 200ms'  // Rozwijanie: 600ms + 200ms delay jak SearchBar
          : 'top 600ms cubic-bezier(0.25, 1, 0.2, 1)',        // Zwijanie: 600ms bez delay
      }}>
        <EmployeeList
          onSelectEmployee={(name) => { selectedEmployeeRef.current = name; conv.setSelectedEmployee(name); setMicStatus('default'); setSearchQuery(''); setSearchAnimated(false); setSearchCompleted(false); searchAnimatingRef.current = false }}
          searchQuery={searchQuery}
          searchAnimated={searchAnimated}
          searchCompleted={searchCompleted}
          noteUpdate={conv.noteUpdate}
          apiEmployees={conv.apiEmployees}
          conversationItems={conv.conversationItems}
          onDeleteEmployee={handleDeleteEmployee}
          onDeleteConversation={handleDeleteConversation}
          onInteraction={resetIdleTimer}
          resetSignal={resetSignal}
          onSearchAnimationEnd={handleSearchAnimationEnd}
          searchPipelineState={search.state}
          onModalStateChange={setListModalOpen}
        />
      </div>

      {/* E1: SearchBar w ŚRODKU - rozwija się W GÓRĘ do listy kafelków */}
      <div style={{
        position: 'absolute',
        top: `${positions.searchBarTop}%`,
        left: '4.8%', right: '4.8%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        zIndex: 30,
      }}>
        <SearchBar
          status={micStatus}
          expandDirection="up"  // Rozwija się w GÓRĘ
          expandHeight={micStatus === 'working' ? positions.searchBarBottomExpand : 0}
          value={micStatus === 'working' ? conv.transcriptionText : micStatus === 'pushed' ? searchQuery : undefined}
          selectedEmployee={conv.selectedEmployee}
          onChange={(val) => conv.setTranscriptionText(val)}
          onSearchSubmit={(name) => {
            const q = name.trim()
            if (q) {
              searchAnimatingRef.current = true
              setSearchAnimated(true)
              setSearchCompleted(false)
              search.startSearch(q)
            }
            selectedEmployeeRef.current = ''
            conv.setSelectedEmployee('')
            setMicStatus('default')
          }}
          onSearchChange={(val) => { selectedEmployeeRef.current = ''; conv.setSelectedEmployee(''); setSearchQuery(val) }}
          onVoiceSearchResult={handleVoiceSearchResult}
        />
      </div>

      {/* E1: DuzyMic NA DOLE - STAŁA pozycja (nie zmienia się przy dyktowaniu) */}
      <div style={{
        position: 'absolute',
        top: `${positions.mlotButtonTop}%`,  // Stała pozycja
        left: 0, right: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        zIndex: 40,
      }}>
        <MlotButton
          status={micStatus}
          scale={micScale}
          onVoiceSearch={handleVoiceSearch}
          onDictationStart={handleDictationStart}
          onDictationEnd={handleDictationEnd}
          onWarmup={() => { if (isOnline) dictation.warmup() }}
          onWarmupCancel={() => { if (isOnline) dictation.cancel(); else { offlineSpeechRef.current?.stop(); offlineSpeechRef.current = null } }}
          onDebug={dbg}
        />
      </div>

      {/* TokenBar na dole */}
      <div style={{
        position: 'absolute',
        bottom: `${positions.tokenBarBottom}%`,
        left: '4.8%', right: '4.8%',
        zIndex: 10,
      }}>
        <SyncStatusBar pendingCount={pendingSyncCount} syncing={syncingNow} dimmed={!isOnline} />
      </div>

    </div>
  )
}
